import { useRef, useState } from 'react';
import {
  Check,
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Star,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';
import {
  formatearTamano,
  TIPO_DOCUMENTO_LABELS,
  type Candidato,
  type DocumentoCandidato,
  type TipoDocumento,
} from '@/lib/candidatos';
import VisorDocumento from './VisorDocumento';

/**
 * Los ficheros del candidato: currículums, cartas y certificados.
 *
 * Se sube arrastrando o desde el selector, y en el móvil también con la cámara:
 * la mitad de los CV que llegan son una foto de un papel hecha en la oficina, y
 * obligar a que alguien lo escanee primero es garantizar que no se suba nunca.
 */
interface Props {
  candidato: Candidato;
  onActualizado: (c: Candidato) => void;
}

const TIPOS: TipoDocumento[] = ['cv', 'carta', 'certificado', 'otro'];

function IconoDocumento({ doc }: { doc: DocumentoCandidato }) {
  const esFoto = (doc.contentType || '').startsWith('image/');
  const Icono = esFoto ? ImageIcon : FileText;
  return <Icono size={16} className="text-sereno-dark/40 flex-shrink-0" />;
}

export default function DocumentosCandidato({ candidato, onActualizado }: Props) {
  const [tipo, setTipo] = useState<TipoDocumento>('cv');
  const [subiendo, setSubiendo] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [viendo, setViendo] = useState<DocumentoCandidato | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const camaraRef = useRef<HTMLInputElement>(null);

  const documentos = candidato.documentos || [];

  const subir = async (files: FileList | File[] | null) => {
    const lista = Array.from(files || []);
    if (!lista.length) return;

    setSubiendo(true);
    setError(null);
    try {
      onActualizado(await api.subirDocumentosCandidato(candidato.id, lista, tipo));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido subir el archivo');
    } finally {
      setSubiendo(false);
      if (inputRef.current) inputRef.current.value = '';
      if (camaraRef.current) camaraRef.current.value = '';
    }
  };

  const conOcupado = async (docId: string, accion: () => Promise<Candidato>) => {
    setOcupado(docId);
    setError(null);
    try {
      onActualizado(await accion());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido completar la acción');
    } finally {
      setOcupado(null);
    }
  };

  const borrar = (doc: DocumentoCandidato) => {
    if (!confirm(`¿Eliminar "${doc.fileName}"? No se puede deshacer.`)) return;
    void conOcupado(doc.id, () => api.borrarDocumentoCandidato(candidato.id, doc.id));
  };

  const marcarPrincipal = (doc: DocumentoCandidato) =>
    conOcupado(doc.id, () =>
      api.actualizarDocumentoCandidato(candidato.id, doc.id, { principal: true }),
    );

  const renombrar = async (doc: DocumentoCandidato) => {
    const limpio = nombreNuevo.trim();
    if (!limpio || limpio === doc.fileName) return setRenombrando(null);
    await conOcupado(doc.id, () =>
      api.actualizarDocumentoCandidato(candidato.id, doc.id, { fileName: limpio }),
    );
    setRenombrando(null);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-medium">Documentos</h2>
        <span className="text-xs text-sereno-dark/40">{documentos.length}</span>
      </div>

      {/* Tipo con el que entran los ficheros que se suban ahora */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {TIPOS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTipo(t)}
            aria-pressed={tipo === t}
            className={classNames(
              'h-9 px-3 rounded-lg border text-xs font-medium transition-colors',
              tipo === t
                ? 'border-sereno-dark bg-sereno-dark text-white'
                : 'border-gray-200 bg-white text-sereno-dark/60 hover:border-sereno-dark/40',
            )}
          >
            {TIPO_DOCUMENTO_LABELS[t]}
          </button>
        ))}
      </div>

      <label
        className={classNames(
          'flex flex-col items-center justify-center gap-1.5 w-full rounded-xl border-2 border-dashed px-4 py-5 cursor-pointer transition-colors',
          arrastrando
            ? 'border-sereno-blue bg-sereno-blue/10 text-sereno-dark/70'
            : 'border-sereno-dark/15 hover:border-sereno-blue/50 hover:bg-sereno-blue/5 text-sereno-dark/30 hover:text-sereno-dark/50',
          subiendo && 'pointer-events-none opacity-60',
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => {
          e.preventDefault();
          setArrastrando(false);
          void subir(e.dataTransfer.files);
        }}
      >
        {subiendo ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
        <span className="text-xs text-center leading-tight">
          {subiendo ? (
            'Subiendo…'
          ) : (
            <>
              <span className="font-medium">Arrastra archivos</span> o haz clic
            </>
          )}
        </span>
        <span className="text-[11px] text-sereno-dark/30">PDF, Word o imágenes · hasta 15 MB</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
          onChange={(e) => void subir(e.target.files)}
          disabled={subiendo}
        />
      </label>

      {/* La cámara solo en móvil: en escritorio no hay nada que fotografiar */}
      <button
        type="button"
        onClick={() => camaraRef.current?.click()}
        disabled={subiendo}
        className="btn-ghost w-full mt-2 h-11 text-sm flex items-center justify-center gap-2 sm:hidden"
      >
        <ImageIcon size={16} /> Hacer una foto del documento
      </button>
      <input
        ref={camaraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void subir(e.target.files)}
      />

      {error && (
        <p className="text-sm text-red-600 mt-2" role="alert">
          {error}
        </p>
      )}

      {documentos.length === 0 ? (
        <p className="text-xs text-sereno-dark/25 mt-3">Sin documentos todavía.</p>
      ) : (
        <ul className="mt-3 space-y-1">
          {documentos.map((doc) => {
            const esPrincipal = candidato.cvPrincipalId === doc.id;
            return (
              <li
                key={doc.id}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-sereno-blue/10 group text-sm"
              >
                <IconoDocumento doc={doc} />

                <div className="flex-1 min-w-0">
                  {renombrando === doc.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        className="input text-sm py-1 px-2 flex-1 min-w-0"
                        value={nombreNuevo}
                        onChange={(e) => setNombreNuevo(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void renombrar(doc);
                          if (e.key === 'Escape') setRenombrando(null);
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => void renombrar(doc)}
                        className="p-1.5 rounded hover:bg-green-50 text-green-600"
                        aria-label="Guardar el nombre"
                      >
                        {ocupado === doc.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Check size={14} />
                        )}
                      </button>
                      <button
                        onClick={() => setRenombrando(null)}
                        className="p-1.5 rounded hover:bg-gray-100 text-sereno-dark/40"
                        aria-label="Cancelar"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="truncate flex items-center gap-1.5">
                        {doc.fileName}
                        {esPrincipal && (
                          <Star
                            size={12}
                            className="text-amber-500 flex-shrink-0"
                            fill="currentColor"
                            aria-label="Currículum principal"
                          />
                        )}
                      </p>
                      <p className="text-xs text-sereno-dark/30">
                        {TIPO_DOCUMENTO_LABELS[doc.tipo]} · {formatearTamano(doc.size)}
                        {doc.extraccion === 'sin_texto' && ' · sin texto legible'}
                      </p>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-0.5 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                  {doc.tipo === 'cv' && !esPrincipal && (
                    <button
                      onClick={() => void marcarPrincipal(doc)}
                      className="p-1.5 rounded hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-amber-500"
                      title="Marcar como currículum principal"
                    >
                      {ocupado === doc.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Star size={14} />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setRenombrando(doc.id);
                      setNombreNuevo(doc.fileName);
                    }}
                    className="p-1.5 rounded hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60"
                    title="Renombrar"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => setViendo(doc)}
                    className="p-1.5 rounded hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60"
                    title="Ver"
                  >
                    <Eye size={14} />
                  </button>
                  <a
                    href={api.urlDocumentoCandidato(candidato.id, doc.id, true)}
                    className="p-1.5 rounded hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60"
                    title="Descargar"
                  >
                    <Download size={14} />
                  </a>
                  <button
                    onClick={() => borrar(doc)}
                    disabled={ocupado === doc.id}
                    className="p-1.5 rounded hover:bg-red-50 text-sereno-dark/30 hover:text-red-500"
                    title="Eliminar"
                  >
                    {ocupado === doc.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {viendo && (
        <VisorDocumento
          candidatoId={candidato.id}
          documento={viendo}
          onClose={() => setViendo(null)}
        />
      )}
    </div>
  );
}
